import { captureError } from "@outschool/platform";
import type { StripeConnectInstance } from "@stripe/connect-js";
import React from "react";

interface StripeConnectContextValue {
  registerInstance: (instance: StripeConnectInstance) => void;
  logoutAll: () => void;
}

const StripeConnectContext = React.createContext<
  StripeConnectContextValue | undefined
>(undefined);

export const StripeConnectProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const instancesRef = React.useRef<Set<StripeConnectInstance>>(new Set());

  const registerInstance = React.useCallback(
    (instance: StripeConnectInstance) => {
      instancesRef.current.add(instance);
    },
    [],
  );

  const logoutAll = React.useCallback(() => {
    instancesRef.current.forEach(instance => {
      try {
        instance.logout();
      } catch (error) {
        captureError(error, {
          extra: {
            message: "Failed to logout Stripe Connect instance",
          },
        });
      }
    });
    instancesRef.current.clear();
  }, []);

  const value = React.useMemo(
    () => ({
      registerInstance,
      logoutAll,
    }),
    [registerInstance, logoutAll],
  );

  return (
    <StripeConnectContext.Provider value={value}>
      {children}
    </StripeConnectContext.Provider>
  );
};

export const useStripeConnectContext = () => {
  const context = React.useContext(StripeConnectContext);
  if (!context) {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "useStripeConnectContext must be used within StripeConnectProvider",
      );
    }
    // Return safe no-ops to prevent crashes
    return {
      registerInstance: () => {},
      logoutAll: () => {},
    };
  }
  return context;
};
